import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { Avatar } from '@kira/checkpoint-shared-frontend';
import React, { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonVariant } from 'components/Button/Button';
import Initials from 'components/Header/Initials';
import Intercom from 'components/Intercom/Intercom';
import { API_URL, NECTAR_URL } from 'config/app.endpoints';
import { t } from 'locales/i18n';
import { UserContext } from 'providers/UserProvider';

interface HeaderProps {
  children?: React.ReactNode;
  showAvatar?: boolean;
  showBackground?: boolean;
  assessmentUid?: string;
}

const Header = ({
  children,
  showAvatar,
  showBackground,
  assessmentUid,
}: HeaderProps) => {
  const {
    userProfile: { initials, photoUrl },
  } = useContext(UserContext);

  const navContainer = twMerge(
    'px-4',
    'mx-auto',
    'max-w',
    !!showBackground && 'bg-white shadow-[0_0_1.25rem_0_rgba(0,0,0,0.05)]'
  );

  const navInnerWrapper = twMerge(
    'flex',
    'justify-between',
    'lg:border-none',
    'w-full',
    '2xl:container',
    '2xl:mx-auto',
    '2xl:px-4',
    !!children && 'border-b border-slate-300'
  );

  const childrenWrapperDesktop = twMerge(
    'hidden',
    'items-center',
    'lg:flex',
    'lg:flex-1',
    'lg:justify-center',
    'grow',
    'gap-2'
  );

  const childrenWrapperMobile = twMerge(
    'flex',
    'flex-wrap',
    'justify-center',
    'space-x-6',
    'lg:hidden',
    'gap-2'
  );

  return (
    <header className="relative z-10">
      <nav className={navContainer} aria-label="Top">
        <div className={navInnerWrapper}>
          <div className="flex py-2 lg:basis-1/4">
            <a
              href={API_URL}
              className="flex cursor-pointer items-center gap-1"
            >
              <ChevronLeftIcon className="h-4 w-4 text-kira-slate-700" />
              <span className="prose-body-md-med text-kira-slate-700">
                {t('Exit')}
              </span>
            </a>
          </div>
          {children && <div className={childrenWrapperDesktop}>{children}</div>}
          <div className="flex items-center justify-end gap-4 py-2 lg:basis-1/4">
            <a
              href={`${NECTAR_URL}assessment/${assessmentUid}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant={ButtonVariant.TERTIARY}
                type="button"
                buttonText={t('Preview')}
              />
            </a>
            <div className="space-x-4">
              <Intercom />
            </div>
            {showAvatar && photoUrl ? (
              <Avatar
                avatarClassName="h-9 w-9 cursor-pointer bg-slate-300"
                src={photoUrl}
              />
            ) : (
              <Initials content={initials} />
            )}
          </div>
        </div>
        {children && <div className={childrenWrapperMobile}>{children}</div>}
      </nav>
    </header>
  );
};

export default Header;
